import { useQuery } from '@tanstack/react-query'
import { supabase, supabaseReady } from '@/lib/supabase'
import { isRealAthleteId } from './useSetLogs'

export interface TestingResult {
  id: string
  test_id: string
  date: string
  values: Record<string, number | null>
  flag_status?: string | null
  flag_reason?: string | null
}

/**
 * VALD testing results for one athlete, newest first. RLS already limits this to
 * the athlete's own rows (or everything, for a coach).
 */
export function useTestingResults(athleteId: string, enabled = true) {
  return useQuery({
    queryKey: ['testing-results', athleteId],
    enabled: enabled && supabaseReady && isRealAthleteId(athleteId),
    queryFn: async (): Promise<TestingResult[]> => {
      const { data, error } = await supabase
        .from('testing_results')
        .select('id,test_id,date,values,flag_status,flag_reason')
        .eq('athlete_id', athleteId)
        .eq('is_best_trial', true)
        .order('date', { ascending: false })
      if (error) throw error
      return (data ?? []) as TestingResult[]
    },
  })
}

/**
 * Persist a computed flag onto the athlete's most recent result row, so flags
 * are queryable in Supabase rather than only existing at render time
 * (brief task 5). Coach-only — athletes have read access to these rows.
 */
export async function persistFlag(rowId: string, status: string, reason: string) {
  // Must be awaited: supabase query builders never fire otherwise.
  const { error } = await supabase
    .from('testing_results')
    .update({ flag_status: status, flag_reason: reason })
    .eq('id', rowId)
  if (error) console.error('Could not save testing flag:', error.message)
}

/**
 * Just the date of an athlete's most recent test. Deliberately light — the gym
 * view renders four of these at once and doesn't need full history.
 * Returns null when they've never been tested.
 */
export function useLastTestDate(athleteId: string, enabled = true) {
  return useQuery({
    queryKey: ['last-test-date', athleteId],
    enabled: enabled && supabaseReady && isRealAthleteId(athleteId),
    queryFn: async (): Promise<string | null> => {
      const { data, error } = await supabase
        .from('testing_results')
        .select('date')
        .eq('athlete_id', athleteId)
        .order('date', { ascending: false })
        .limit(1)
      if (error) throw error
      return data?.[0]?.date ?? null
    },
  })
}
